import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useOutletContext, useNavigate, useLocation } from "react-router-dom";
import BottomBar from "../Includes/BottomBar";
import RightPanel from "../Includes/RightPanel";
import { GPSModal, MapPopupModal, DriversPanel, VehiclesPanel, ReportsPanel, SettingsPanel, DocumentsPanel } from "../Includes/Modals";

// ── Live Tracking Map — fleet view ──────────────────────────────────────────
// Plots every load with a known GPS fix as its own marker (color-coded by
// status, labeled with the job #). Clicking a vehicle icon opens a Leaflet
// popup with that driver's info + selects the load (which drives the right
// panel / mapPopupDrv card). Positions are pushed in live via postMessage
// whenever `loads` changes — including from the dispatch-hub WebSocket,
// which updates every vehicle on the fleet, not just the selected one.
const STATUS_COLORS = {
  "In Progress": "#2563eb", // blue
  "Pending":     "#f59e0b", // amber
  "Completed":   "#16a34a", // green
  "Delayed":     "#dc2626", // red
};

function LiveTrackingMap({ loads = [], selectedLoadId, onSelectVehicle, darkMode = false }) {
  const iframeRef = useRef(null);
  const [ready, setReady] = useState(false);

  const trackable = loads.filter(l => l.lat != null && l.lng != null);

  useEffect(() => {
    if (!ready || !iframeRef.current) return;
    iframeRef.current.contentWindow.postMessage({
      type: "UPDATE_MARKERS",
      markers: trackable.map(l => ({
        id: l.rawId, jobId: l.id, driver: l.driver, status: l.status,
        lat: l.lat, lng: l.lng, speed: l.speed, eta: l.eta, lastUpdate: l.lastUpdate,
        color: STATUS_COLORS[l.status] || "#64748b",
      })),
      selectedId: selectedLoadId,
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, "*");
  }, [ready, JSON.stringify(trackable), selectedLoadId]);

  useEffect(() => {
    if (!ready || !iframeRef.current) return;
    iframeRef.current.contentWindow.postMessage({ type: "SET_THEME", dark: darkMode }, "*");
  }, [ready, darkMode]);

  useEffect(() => {
    const onMsg = (e) => {
      if (e.data?.type === "MAP_READY") setReady(true);
      if (e.data?.type === "SELECT_VEHICLE") onSelectVehicle?.(e.data.id);
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [onSelectVehicle]);

  // ── FIX: flickering / "refreshing" map ──────────────────────────────────
  // The previous version baked the current vehicle's lat/lng directly into
  // this HTML string (`setView([${centerPt}...`) to center the map on
  // first load. Because that string is regenerated on every render, and a
  // React iframe reloads its ENTIRE document any time `srcDoc` changes,
  // the whole map — tiles, markers, zoom/pan state — was being torn down
  // and rebuilt from scratch on every single GPS update (every poll tick,
  // every live WS position push). That's the flicker. Fixed by building
  // this string exactly once (useState lazy-init, no reactive values
  // inside it) and doing every subsequent update — including the one-time
  // "zoom to fit all vehicles" on first data — via postMessage into the
  // already-running map instead of regenerating the page.
  const [mapHtml] = useState(() => `<!DOCTYPE html>
<html><head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css"/>
<script src="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js"></script>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  html, body, #map { width: 100%; height: 100%; background: #f8fafc; }
  body.dark, body.dark #map { background: #0f172a; }

  .veh-label-wrapper {
    position: absolute; bottom: 22px; left: 50%; transform: translateX(-50%);
    background: #111827; color: #fff; font: 600 13px/1 -apple-system, sans-serif;
    padding: 7px 12px; border-radius: 16px; white-space: nowrap;
    box-shadow: 0 4px 10px rgba(0,0,0,0.3); pointer-events: none;
  }
  .veh-label-wrapper::after {
    content: ''; position: absolute; top: 100%; left: 50%; transform: translateX(-50%);
    border-width: 6px; border-style: solid; border-color: #111827 transparent transparent transparent;
  }

  .veh-pop { font: 13px/1.4 -apple-system, sans-serif; min-width: 170px; }
  .veh-pop b { display: block; font-size: 13px; margin-bottom: 2px; }
  .veh-pop .row { color: #6b7280; font-size: 11px; margin-top: 2px; }
  .veh-pop .row b { display: inline; font-size: 11px; color: #111827; }
  body.dark .leaflet-popup-content-wrapper, body.dark .leaflet-popup-tip { background: #1e293b; color: #e2e8f0; }
  body.dark .veh-pop .row { color: #94a3b8; }
  body.dark .veh-pop .row b, body.dark .veh-pop b { color: #f1f5f9; }

  @media (max-width: 600px) {
    .veh-label-wrapper { font-size: 11px; padding: 5px 10px; bottom: 20px; }
    .veh-label-wrapper::after { border-width: 5px; }
    .veh-pop { min-width: 140px; font-size: 12px; }
    .veh-pop b { font-size: 12px; }
    .veh-pop .row, .veh-pop .row b { font-size: 10px; }
    .leaflet-control-zoom a { width: 26px !important; height: 26px !important; line-height: 26px !important; }
  }
</style>
</head><body>
<div id="map"></div>
<script>
  // Generic default view (continental US) — real position comes via
  // postMessage a moment later and fit-to-bounds happens once then.
  var map = L.map('map', {zoomControl:true, attributionControl:false}).setView([39.8283, -98.5795], 4);
  var tileLight = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {maxZoom:19});
  var tileDark  = L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {maxZoom:19, subdomains:'abcd'});
  var currentTile = tileLight.addTo(map);

  var markers = {}; // id -> {marker, label}
  var hasFitOnce = false;

  function iconFor(color, selected) {
    var size = selected ? 30 : 24;
    return L.divIcon({
      html: '<div style="background:'+color+'; width:'+size+'px; height:'+size+'px; border-radius:50%; border:3px solid white; box-shadow:0 2px 8px rgba(0,0,0,0.45); display:flex; align-items:center; justify-content:center;' + (selected ? 'outline:3px solid '+color+'55;' : '') + '"><svg width="'+(size-12)+'" height="'+(size-12)+'" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13"></rect><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon><circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="18.5" cy="18.5" r="2.5"></circle></svg></div>',
      iconAnchor: [size/2, size/2], className: ''
    });
  }

  function popupHtml(m) {
    return '<div class="veh-pop"><b>'+m.jobId+'</b>'
      + '<div class="row">Driver: <b>'+(m.driver||'Unassigned')+'</b></div>'
      + '<div class="row">Status: <b>'+m.status+'</b></div>'
      + '<div class="row">Speed: <b>'+(m.speed!=null? m.speed+' mph':'—')+'</b></div>'
      + '<div class="row">ETA: <b>'+(m.eta||'—')+'</b></div>'
      + '<div class="row">Updated: <b>'+(m.lastUpdate||'—')+'</b></div></div>';
  }

  function getLabelIcon(m) {
    return L.divIcon({
      html: '<div class="veh-label-wrapper">' + m.jobId + (m.driver ? ' &middot; ' + m.driver.split(" ")[0] : '') + '</div>',
      iconSize: [0, 0], iconAnchor: [0, 0], className: ''
    });
  }

  function upsertMarker(m, selected) {
    var existing = markers[m.id];
    if (existing) {
      existing.marker.setLatLng([m.lat, m.lng]);
      existing.marker.setIcon(iconFor(m.color, selected));
      existing.marker.setPopupContent(popupHtml(m));
      existing.label.setLatLng([m.lat, m.lng]);
      existing.label.setIcon(getLabelIcon(m));
    } else {
      var marker = L.marker([m.lat, m.lng], {icon: iconFor(m.color, selected)}).addTo(map);
      marker.bindPopup(popupHtml(m));
      marker.on('click', function() {
        window.parent.postMessage({type:'SELECT_VEHICLE', id: m.id}, '*');
      });
      var label = L.marker([m.lat, m.lng], {
        icon: getLabelIcon(m), interactive: false, zIndexOffset: 1000
      }).addTo(map);
      markers[m.id] = {marker: marker, label: label};
    }
  }

  window.addEventListener('message', function(e) {
    if (e.data.type === 'SET_THEME') {
      var wantDark = !!e.data.dark;
      document.body.classList.toggle('dark', wantDark);
      var want = wantDark ? tileDark : tileLight;
      if (want !== currentTile) { map.removeLayer(currentTile); want.addTo(map); currentTile = want; }
      return;
    }
    if (e.data.type === 'UPDATE_MARKERS') {
      var incoming = e.data.markers || [];
      var incomingIds = {};
      incoming.forEach(function(m) {
        incomingIds[m.id] = true;
        upsertMarker(m, String(m.id) === String(e.data.selectedId));
      });
      Object.keys(markers).forEach(function(id) {
        if (!incomingIds[id]) {
          map.removeLayer(markers[id].marker);
          map.removeLayer(markers[id].label);
          delete markers[id];
        }
      });
      // Only auto-fit the view the FIRST time real marker data shows up —
      // never again after that, or the map would keep re-centering on
      // every update and undo the dispatcher's own pan/zoom (this was
      // part of what made it feel like it kept "resetting").
      if (!hasFitOnce && incoming.length > 0) {
        hasFitOnce = true;
        var bounds = L.latLngBounds(incoming.map(function(m){ return [m.lat, m.lng]; }));
        map.fitBounds(bounds, {padding: [50, 50], maxZoom: 12});
      }
    }
  });

  window.parent.postMessage({type:'MAP_READY'}, '*');
</script>
</body></html>`);

  return (
    <div className="w-full h-full bg-slate-50 dark:bg-slate-900 relative">
      <iframe ref={iframeRef} srcDoc={mapHtml} onLoad={() => setReady(true)} style={{ width: "100%", height: "100%", border: "none", display: "block" }} title="Live Tracking Map" />
      {trackable.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center text-gray-400 dark:text-gray-500 font-medium text-sm bg-slate-50/80 dark:bg-slate-900/80 pointer-events-none">
          No vehicles reporting GPS yet
        </div>
      )}
    </div>
  );
}

// ── UI Components ─────────────────────────────────────────────────────────────
function StatusPill({ status }) {
  const map = {"In Progress":"bg-blue-100 text-blue-700", Pending:"bg-orange-100 text-orange-600", Completed:"bg-green-100 text-green-700", Delayed:"bg-red-100 text-red-600"};
  return <span className={`text-xs px-2 py-0.5 rounded font-semibold whitespace-nowrap ${map[status]||"bg-gray-100 text-gray-600"}`}>{status}</span>;
}

function OrderRow({ load, selected, onClick }) {
  const iconBg    = load.status==="In Progress"?"bg-blue-100":load.status==="Pending"?"bg-orange-100":load.status==="Completed"?"bg-green-100":"bg-red-100";
  const iconColor = load.status==="In Progress"?"text-blue-600":load.status==="Pending"?"text-orange-500":load.status==="Completed"?"text-green-600":"text-red-500";
  return (
    <div onClick={onClick} className={`flex items-start gap-3 px-4 py-3 cursor-pointer border-b border-gray-100 transition-colors ${selected?"bg-blue-50 border-l-2 border-l-blue-600":"hover:bg-gray-50"}`}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${iconBg}`}>
        <svg className={`w-5 h-5 ${iconColor}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 17H5a2 2 0 01-2-2V5a2 2 0 012-2h11a2 2 0 012 2v3m-1 11a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2h8z"/>
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className="text-sm font-semibold text-gray-900">{load.id}</span>
          <StatusPill status={load.status}/>
        </div>
        <p className="text-xs text-gray-500 mt-0.5 truncate">{load.origin} → {load.destination}</p>
        <div className="flex items-center justify-between mt-1">
          <span className="text-xs text-gray-500">Driver: {load.driver}</span>
          {load.status === "In Progress" && (
            <span className="text-xs font-medium text-gray-700 flex items-center gap-1">
              Live <span className="w-1.5 h-1.5 bg-green-500 rounded-full inline-block animate-pulse"/>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ c, compact = false, onClick }) {
  const bg = c.color==="blue"?"bg-blue-50":c.color==="green"?"bg-green-50":c.color==="red"?"bg-red-50":"bg-purple-50";
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      className={`text-left w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-3 lg:p-4 transition-all ${onClick ? "hover:border-blue-300 hover:shadow-md cursor-pointer" : ""}`}
    >
      <div className="flex items-start justify-between mb-1.5 lg:mb-2">
        <div className={`${compact?"w-9 h-9":"w-10 h-10"} rounded-xl flex items-center justify-center text-base lg:text-lg ${bg}`}>{c.icon}</div>
        {c.delta && <span className={`text-xs font-semibold ${c.delta.includes("↓")?"text-red-500":"text-green-600"}`}>{c.delta}</span>}
      </div>
      <p className={`text-gray-400 font-medium ${compact?"text-[10px]":"text-xs"}`}>{c.label}</p>
      <p className={`font-bold text-gray-900 mt-0.5 ${compact?"text-xl":"text-2xl"}`}>{c.val}</p>
      {c.prog != null && (
        <div className={compact?"mt-1.5":"mt-2"}>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full" style={{width:`${c.prog}%`}}/>
          </div>
          <p className={`text-gray-400 text-right ${compact?"text-[10px] mt-0.5":"text-xs mt-1"}`}>{c.prog}%</p>
        </div>
      )}
    </button>
  );
}

// ── Small data helpers ──────────────────────────────────────────────────────
// Great-circle distance in miles between two [lat,lng]-ish points.
function haversineMiles(lat1, lng1, lat2, lng2) {
  if ([lat1, lng1, lat2, lng2].some(v => v === null || v === undefined || Number.isNaN(v))) return null;
  const r = 3958.8;
  const toRad = d => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * r * Math.asin(Math.min(1, Math.sqrt(a)));
}

function timeAgo(iso) {
  if (!iso) return "—";
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function fmtClock(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

// Turns raw /api/loads/assignments/mine/ rows into the shape the UI needs.
// IMPORTANT: field names below must match LoadAssignmentSerializer /
// LoadBoardSerializer on the backend exactly (see loads/serializers.py).
function mapAssignment(a) {
  let uiStatus = "Pending";
  if (["started", "in_transit", "paused"].includes(a.status)) uiStatus = "In Progress";
  if (a.status === "completed") uiStatus = "Completed";
  if (a.status === "cancelled") uiStatus = "Delayed";

  const start = a.load?.startCoords || null; // [lat, lng]
  const end = a.load?.endCoords || null;     // [lat, lng]

  const totalMiles = start && end ? haversineMiles(start[0], start[1], end[0], end[1]) : null;
  const remainingMiles =
    end && a.last_lat != null && a.last_lng != null ? haversineMiles(a.last_lat, a.last_lng, end[0], end[1]) : null;

  let progress = 0;
  if (uiStatus === "Completed") progress = 100;
  else if (totalMiles && remainingMiles != null && totalMiles > 0) {
    progress = Math.max(0, Math.min(100, Math.round(((totalMiles - remainingMiles) / totalMiles) * 100)));
  }

  let etaLabel = "—";
  if (remainingMiles != null && a.last_speed > 5) {
    const hours = remainingMiles / a.last_speed;
    etaLabel = fmtClock(new Date(Date.now() + hours * 3600 * 1000).toISOString());
  }

  return {
    id: a.load?.id || `PLU-${String(a.id).slice(0, 6)}`, // load.id is the reference code (see LoadBoardSerializer)
    loadPk: a.load?.pk,
    rawId: a.id,
    status: uiStatus,
    origin: a.load?.from || "Origin not set",
    destination: a.load?.to || "Destination not set",
    driver: a.driver?.full_name || "Unassigned",
    driverId: a.driver?.id,
    driverPhone: a.driver?.phone || null,
    lat: a.last_lat,
    lng: a.last_lng,
    speed: a.last_speed != null ? Math.round(a.last_speed) : null,   // mph, from LoadAssignment.last_speed
    lastUpdate: timeAgo(a.last_ping_at),
    progress,
    remaining: remainingMiles != null ? `${Math.round(remainingMiles)} mi` : "—",
    eta: etaLabel,
    // NOTE: fuel % and cabin temperature are not tracked anywhere in the
    // backend schema (no field on LoadAssignment/DriverLocation). Until a
    // telemetry source is added there is nothing to fetch here — show a
    // dash instead of fabricating a number.
    fuel: null,
    temp: null,
    isTrackingActive: a.is_tracking_active,
    raw: a,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
export default function DispatcherOverview() {
  const { user, darkMode } = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();

  // ── Sidebar deep-links ──────────────────────────────────────────────────
  // The sidebar can't navigate to separate pages for Alerts/Messages (they
  // live inside this component as tabs, not their own routes) or to pages
  // that don't exist yet (Drivers/Vehicles/Reports/Documents/Settings) — see
  // Includes/Sidebar.jsx's ROUTE_FOR map. Instead it deep-links here with a
  // query param this effect reads once on load: `?tab=alerts` opens that
  // right-panel tab, `?soon=<label>` pops the same toast notification used
  // for incoming chat messages, honestly telling the dispatcher that page
  // isn't built yet instead of the click silently doing nothing.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    const soon = params.get("soon");
    const panel = params.get("panel");
    if (tab && ["detail", "chat", "alerts", "messages"].includes(tab)) {
      setRightTab(tab);
    }
    if (soon) {
      pushToast({ driverName: soon, content: "This page isn't built yet — coming soon.", onClick: null });
    }
    if (panel && ["drivers", "vehicles", "reports", "settings", "documents"].includes(panel)) {
      setActivePanel(panel);
    }
    if (tab || soon || panel) {
      navigate("/dispatcher-dashboard", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [mobileView,  setMobileView]  = useState("dashboard");
  const [rightTab,    setRightTab]    = useState("detail");
  const [detailOpen,  setDetailOpen]  = useState(true);
  const [orderFilter, setOrderFilter] = useState("All");

  const [gpsModal,    setGpsModal]    = useState(false);
  const [mapPopupDrv, setMapPopupDrv] = useState(null);
  const [activePanel, setActivePanel] = useState(null); // "drivers" | "vehicles" | "reports" | "settings" | "documents" | null

  const [loads, setLoads] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedLoad, setSelectedLoad] = useState(null);

  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");

  const [alerts, setAlerts] = useState([]);
  const [chatConnected, setChatConnected] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [driverTyping, setDriverTyping] = useState(false);
  const typingTimeoutRef = useRef(null);
  const myTypingSentRef = useRef(false);

  // Pop-up notification for an incoming message — auto-dismisses, click to jump to that chat.
  const pushToast = useCallback((toast) => {
    const id = `t-${Date.now()}-${Math.random()}`;
    setToasts(prev => [...prev, { id, ...toast }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 5000);
  }, []);
  const [gpsHistory, setGpsHistory] = useState([]);

  const chatWsRef = useRef(null);
  const hubWsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const hubReconnectTimeoutRef = useRef(null);
  const isMounted = useRef(true);
  const selectedLoadRef = useRef(null);
  const mapSectionRef = useRef(null);
  const ordersSectionRef = useRef(null);
  useEffect(() => { selectedLoadRef.current = selectedLoad; }, [selectedLoad]);
  const loadsRef = useRef([]);
  useEffect(() => { loadsRef.current = loads; }, [loads]);

  // ── API / WebSocket base URL resolution ─────────────────────────────────
  // REACT_APP_API_URL / REACT_APP_WS_URL are baked in at BUILD time (a
  // classic CRA/webpack gotcha — setting them on the server afterward does
  // nothing, the value has to be present when `npm run build` runs). If
  // they're missing and we're not actually on localhost, silently falling
  // back to 127.0.0.1:8000 means the deployed app tries to talk to the
  // visitor's own machine — every API/WS call fails, with no obvious error
  // beyond "nothing loads". This makes that failure loud instead of silent,
  // and only uses the localhost fallback when we're actually on localhost.
  const isLocalHost = ["localhost", "127.0.0.1"].includes(window.location.hostname);
  const apiUrl = process.env.REACT_APP_API_URL || (isLocalHost ? "http://127.0.0.1:8000" : "");
  const wsDomain = process.env.REACT_APP_WS_URL || (isLocalHost ? "127.0.0.1:8000" : "");
  if (!apiUrl && !isLocalHost) {
    // eslint-disable-next-line no-console
    console.error(
      "[PilotLinkUSA] REACT_APP_API_URL / REACT_APP_WS_URL were not set at build time. " +
      "The dispatcher portal cannot reach the backend — rebuild with these set, e.g. " +
      "REACT_APP_API_URL=https://api.pilotlinkusa.org REACT_APP_WS_URL=api.pilotlinkusa.org npm run build"
    );
  }

  // ── 1. Secure Polling Logic (Background Sync) ──────────────────────────────
  const fetchDashboardData = useCallback(async () => {
    const token = localStorage.getItem("user_token") || localStorage.getItem("access_token");
    if (!token) return;

    try {
      const opts = { headers: { "Authorization": `Bearer ${token}` } };
      
      // Dispatcher endpoints handle filtering automatically based on the token
      const [statsRes, loadsRes] = await Promise.all([
        fetch(`${apiUrl}/api/loads/dispatcher/overview/`, opts),
        fetch(`${apiUrl}/api/loads/assignments/mine/`, opts)
      ]);

      if (!isMounted.current) return;

      if (statsRes.ok) setStats(await statsRes.json());

      if (loadsRes.ok) {
        const rawAssignments = await loadsRes.json();
        const mappedLoads = rawAssignments.map(mapAssignment);

        setLoads(mappedLoads);
        
        setSelectedLoad(currentSelected => {
            if (!currentSelected && mappedLoads.length > 0) return mappedLoads[0];
            if (currentSelected) {
                const updated = mappedLoads.find(l => l.rawId === currentSelected.rawId);
                // Keep local websocket coordinates over polled coordinates for smoothness
                if (updated) return { ...updated, lat: currentSelected.lat || updated.lat, lng: currentSelected.lng || updated.lng };
            }
            return currentSelected;
        });
      }
    } catch (err) { 
        console.error("Dashboard fetch error:", err); 
    }
  }, [apiUrl]);

  useEffect(() => {
    isMounted.current = true;
    fetchDashboardData();
    
    // Refresh the dashboard every 8 seconds
    const interval = setInterval(() => fetchDashboardData(), 8000);
    return () => {
        isMounted.current = false;
        clearInterval(interval);
    };
  }, [fetchDashboardData]);

  // ── Alerts polling (GET /api/loads/dispatcher/alerts/) ─────────────────────
  const fetchAlerts = useCallback(async () => {
    const token = localStorage.getItem("user_token") || localStorage.getItem("access_token");
    if (!token) return;
    try {
      const res = await fetch(`${apiUrl}/api/loads/dispatcher/alerts/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok && isMounted.current) {
        const data = await res.json();
        setAlerts(data.alerts || []);
      }
    } catch (err) {
      console.error("Alerts fetch error:", err);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 15000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  // ── Dispatch Hub — one always-open socket for the whole fleet ──────────────
  // GET/WS /ws/dispatch-hub/?token=<jwt> (see loads/consumers.py::DispatcherHubConsumer).
  // Unlike the per-load chat socket above (which only covers whichever load is
  // selected), this fans out `{type:"hub_update", kind:"gps"|"message",
  // assignment_id, payload}` for EVERY assignment this dispatcher owns. It's
  // what powers the fleet map (every vehicle updates live, not just the
  // selected one) and unread-message badges for conversations you haven't
  // opened yet — the "WhatsApp style" always-on layer.
  useEffect(() => {
    const token = localStorage.getItem("user_token") || localStorage.getItem("access_token");
    if (!token) return;
    let attempt = 0;
    let cancelled = false;

    const connectHub = () => {
      if (cancelled) return;
      const wsProtocol = window.location.protocol === "https:" ? "wss://" : "ws://";
      const ws = new WebSocket(`${wsProtocol}${wsDomain}/ws/dispatch-hub/?token=${token}`);
      hubWsRef.current = ws;

      ws.onopen = () => {
        // BUG FIX: React 18 StrictMode runs this effect twice in dev
        // (mount -> cleanup -> mount) to surface exactly this kind of bug.
        // The old cleanup called `.close()` synchronously on a socket that
        // might still be CONNECTING — closing a socket mid-handshake is
        // ambiguous enough that the server can finish registering it in
        // the Redis group before the close reaches it, leaving a zombie
        // connection alongside the new one (visible in DevTools' Network ->
        // WS panel as two simultaneously-open sockets to the same room).
        // Now cleanup never closes a CONNECTING socket directly — it just
        // marks `cancelled`, and this onopen handler closes it immediately
        // once the handshake actually completes, which is unambiguous.
        if (cancelled) { ws.close(); return; }
        attempt = 0;
      };

      ws.onmessage = (event) => {
        if (cancelled) return;
        const data = JSON.parse(event.data);
        if (data.type !== "hub_update") return;
        const { kind, assignment_id, payload } = data;

        if (kind === "gps") {
          // Live-move every vehicle on the fleet map, not just the open one.
          setLoads(prev => prev.map(l => String(l.rawId) === String(assignment_id)
            ? { ...l, lat: payload.lat, lng: payload.lng, speed: payload.speed != null ? Math.round(payload.speed) : l.speed, lastUpdate: "just now" }
            : l));
          setSelectedLoad(prev => prev && String(prev.rawId) === String(assignment_id)
            ? { ...prev, lat: payload.lat, lng: payload.lng, speed: payload.speed != null ? Math.round(payload.speed) : prev.speed, lastUpdate: "just now" }
            : prev);
        } else if (kind === "message") {
          // Only bump the unread badge if this isn't the conversation
          // currently open in the Chat tab (that one gets the message
          // directly from the per-load socket above).
          const isOpenConversation = selectedLoadRef.current && String(selectedLoadRef.current.rawId) === String(assignment_id);
          if (!isOpenConversation) {
            setLoads(prev => prev.map(l => String(l.rawId) === String(assignment_id)
              ? { ...l, raw: { ...l.raw, unread_count: (l.raw?.unread_count || 0) + 1 }, lastUpdate: "just now" }
              : l));
            // Pop-up notification — this is a conversation the dispatcher
            // hasn't opened, so it wouldn't otherwise be visible at all
            // until they happened to click into it.
            const fromLoad = loadsRef.current.find(l => String(l.rawId) === String(assignment_id));
            pushToast({
              driverName: fromLoad?.driver || payload.sender_name || "Driver",
              content: payload.content,
              onClick: () => fromLoad && handleSelectLoad(fromLoad),
            });
          }
        }
      };

      ws.onclose = () => {
        if (cancelled) return;
        const timeout = Math.min(1000 * (2 ** attempt), 30000);
        attempt++;
        hubReconnectTimeoutRef.current = setTimeout(connectHub, timeout);
      };
    };

    connectHub();
    return () => {
      cancelled = true;
      clearTimeout(hubReconnectTimeoutRef.current);
      const ws = hubWsRef.current;
      if (ws) {
        ws.onclose = null;
        if (ws.readyState === WebSocket.OPEN) {
          ws.close();
        }
        // If it's still CONNECTING, the onopen handler above will close it
        // the instant it finishes — never force-close it from here.
      }
    };
  }, []);

  // ── GPS breadcrumb history for the selected load ────────────────────────────
  // GET /api/loads/assignments/<pk>/gps/history/ -> {points: [{lat,lng,speed,recorded_at}...]}
  useEffect(() => {
    if (!selectedLoad?.rawId) { setGpsHistory([]); return; }
    const token = localStorage.getItem("user_token") || localStorage.getItem("access_token");
    let cancelled = false;

    const loadHistory = () => {
      fetch(`${apiUrl}/api/loads/assignments/${selectedLoad.rawId}/gps/history/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(res => (res.ok ? res.json() : null))
        .then(data => {
          if (cancelled || !data) return;
          const points = data.points || [];
          const rows = [];
          if (data.tracking_started_at) {
            rows.push({ time: fmtClock(data.tracking_started_at), status: "Departed", dot: "green", place: selectedLoad.origin });
          }
          // Sample up to 3 evenly-spaced points from the breadcrumb trail
          if (points.length > 0) {
            const sampleCount = Math.min(3, points.length);
            for (let i = 0; i < sampleCount; i++) {
              const idx = Math.floor((i * (points.length - 1)) / Math.max(1, sampleCount - 1));
              const p = points[idx];
              rows.push({
                time: fmtClock(p.recorded_at),
                status: idx === points.length - 1 ? "Latest" : "In Transit",
                dot: idx === points.length - 1 ? "blue" : "amber",
                place: `${p.lat.toFixed(3)}, ${p.lng.toFixed(3)}`,
              });
            }
          }
          if (selectedLoad.status === "Completed" && data.tracking_stopped_at) {
            rows.push({ time: fmtClock(data.tracking_stopped_at), status: "Arrived", dot: "gray", place: selectedLoad.destination });
          }
          setGpsHistory(rows);
        })
        .catch(err => console.error("GPS history fetch error:", err));
    };

    loadHistory();
    const interval = setInterval(loadHistory, 20000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [selectedLoad?.rawId, selectedLoad?.status, apiUrl]);

  // ── 2. Production-Ready WebSocket Integration (Live Chat & GPS) ──────────────
  // Chat history refresh — used both for the initial load AND as a REST
  // safety net polled every 7s alongside the WebSocket. This means chat
  // still shows up (just not instantly) even if the socket is degraded for
  // any reason, instead of the conversation silently looking empty until a
  // manual page refresh.
  const refreshChatHistory = useCallback(() => {
    if (!selectedLoadRef.current?.rawId) return;
    const token = localStorage.getItem("user_token") || localStorage.getItem("access_token");
    fetch(`${apiUrl}/api/loads/assignments/${selectedLoadRef.current.rawId}/chat/history/`, {
      headers: { "Authorization": `Bearer ${token}` }
    })
      .then(res => res.ok ? res.json() : null)
      .then(serverMsgs => {
        if (!serverMsgs) return;
        setMessages(prev => {
          // Keep any optimistic "pending" bubbles the server list doesn't
          // have yet (e.g. a message sent via the WS fast-path a moment
          // ago that hasn't been persisted-and-re-fetched yet); drop
          // pending bubbles the server has already confirmed so we don't
          // show the same message twice.
          const stillPending = prev.filter(m => m.pending && !serverMsgs.some(sm => sm.content === m.content));
          return [...serverMsgs, ...stillPending];
        });
      })
      .catch(err => console.error("Chat history refresh error:", err));
  }, [apiUrl]);

  useEffect(() => {
    setDriverTyping(false); // don't carry a stale "typing…" over from the previous conversation
    if (!selectedLoad?.rawId) { setMessages([]); return; }
    refreshChatHistory();
    const interval = setInterval(refreshChatHistory, 7000);
    return () => clearInterval(interval);
  }, [selectedLoad?.rawId, refreshChatHistory]);

  useEffect(() => {
    if (!selectedLoad?.rawId) return;

    let attempt = 0;
    let cancelled = false;
    const token = localStorage.getItem("user_token") || localStorage.getItem("access_token");
    const wsProtocol = window.location.protocol === "https:" ? "wss://" : "ws://";

    // Connect strictly to the specific load's chat & GPS hub
    const wsUrl = `${wsProtocol}${wsDomain}/ws/chat/load/${selectedLoad.rawId}/?token=${token}`;

    // B. WebSocket Connection & Reconnection Logic
    const connectWebSocket = () => {
      if (cancelled) return;
      const ws = new WebSocket(wsUrl);
      chatWsRef.current = ws;

      ws.onopen = () => {
        // BUG FIX: this handler used to have no `cancelled` check at all,
        // and the effect's cleanup below used to call `.close()` directly
        // on whatever socket was in chatWsRef — including one still
        // CONNECTING. Two problems stacked on each other:
        //   1. Closing a CONNECTING socket is ambiguous enough that the
        //      server can finish registering it in the Redis group before
        //      the close reaches it — a zombie connection left alongside
        //      the new one (this is what your DevTools Network -> WS panel
        //      showed: two simultaneously-OPEN sockets to the same room,
        //      one idle for 5+ minutes).
        //   2. Worse: onclose had no cancelled guard either, so if that
        //      zombie socket ever DID fire a close event, its reconnect
        //      timer would fire and create ANOTHER new socket — from an
        //      effect invocation React had already torn down. That's a
        //      leak that compounds every time React re-runs this effect
        //      (every StrictMode double-mount in dev, or every real load
        //      switch), which is exactly the endless "Reconnecting…" loop.
        // Now: cleanup never force-closes a CONNECTING socket, it just
        // sets `cancelled`; this handler checks it and closes immediately
        // once the handshake actually completes (an unambiguous state to
        // close from), and every other handler bails out early too.
        if (cancelled) { ws.close(); return; }
        attempt = 0;
        setChatConnected(true);
        // Tell the backend we've opened this conversation so the driver's
        // messages flip from "sent" to "read" on their end, and so we get
        // read_receipt_event back the next time THEY open theirs.
        ws.send(JSON.stringify({ type: "mark_read" }));
      };

      ws.onmessage = (event) => {
        if (cancelled) return;
        const data = JSON.parse(event.data);

        // Handle Real-Time Chat
        // Backend (LoadChatConsumer.chat_message_event) sends the full
        // LoadMessageSerializer payload — {content, sender, sender_name,
        // created_at, ...} — merged with {type:"chat_message"}.
        if (data.type === "chat_message") {
          setMessages(prev => {
            // Reconcile against our own optimistic echo (see handleSendMessage)
            // instead of appending a duplicate: match on the client_id we
            // sent up, falling back to "newest pending message with the
            // same text" if the server doesn't echo client_id back.
            const byClientId = data.client_id && prev.findIndex(m => m.id === data.client_id);
            const byHeuristic = prev.findIndex(m => m.pending && m.content === data.content);
            const idx = (byClientId != null && byClientId > -1) ? byClientId : byHeuristic;
            if (idx > -1) {
              const next = [...prev];
              next[idx] = data;
              return next;
            }
            return [...prev, data];
          });
          // Pop-up notification for an incoming message from the driver
          // (skip our own messages — those are the optimistic echo above).
          const senderId = typeof data.sender === "object" ? data.sender?.id : data.sender;
          if (String(senderId) !== String(user?.id)) {
            pushToast({
              driverName: data.sender_name || selectedLoadRef.current?.driver || "Driver",
              content: data.content,
              onClick: () => setRightTab("chat"),
            });
            // This conversation is open right now, so immediately tell the
            // backend we've read it — no reason to wait for the dispatcher
            // to reopen a chat they're already looking at.
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: "mark_read" }));
            }
          }
        }
        // Handle Real-Time GPS Tracking
        // NOTE: the backend broadcasts this as type "gps_position" (see
        // LoadChatConsumer.gps_position_event) — "gps_update"/"gps_update_event"
        // never fire, which is why the live map position + speed/updated
        // fields silently never refreshed.
        else if (data.type === "gps_position") {
          setSelectedLoad(prev => prev && prev.rawId === selectedLoad.rawId
            ? { ...prev, lat: data.lat, lng: data.lng, speed: data.speed != null ? Math.round(data.speed) : prev.speed, lastUpdate: "just now" }
            : prev);
          setLoads(prevLoads => prevLoads.map(l => l.rawId === selectedLoad.rawId
            ? { ...l, lat: data.lat, lng: data.lng, speed: data.speed != null ? Math.round(data.speed) : l.speed, lastUpdate: "just now" }
            : l));
        }
        // Handle Real-Time Job Status Changes (Driver hits Start/Complete)
        // Backend sends type "job_status" (see LoadChatConsumer.job_status_event).
        else if (data.type === "job_status") {
          fetchDashboardData();
        }
        // Handle Toggle Events (Tracking Paused/Resumed)
        // Backend sends type "tracking_toggle" (see LoadChatConsumer.tracking_toggle_event).
        else if (data.type === "tracking_toggle") {
            setSelectedLoad(prev => prev ? { ...prev, isTrackingActive: data.is_tracking_active, raw: { ...prev.raw, is_tracking_active: data.is_tracking_active } } : prev);
        }
        // Read receipts — backend already fully supports this
        // (LoadChatConsumer "mark_read" -> "read_receipt_event"), it just
        // wasn't wired up here before. When the OTHER party (the driver)
        // reads the conversation, every message currently shown gets
        // marked is_read so the "sent" single check becomes a "read"
        // double check, WhatsApp/OfferUp-style.
        else if (data.type === "read_receipt") {
          if (String(data.reader_id) !== String(user?.id)) {
            setMessages(prev => prev.map(m => ({ ...m, is_read: true })));
          }
        }
        // Typing indicator — same story, backend already sends this.
        else if (data.type === "typing") {
          setDriverTyping(!!data.is_typing);
          clearTimeout(typingTimeoutRef.current);
          if (data.is_typing) {
            typingTimeoutRef.current = setTimeout(() => setDriverTyping(false), 6000);
          }
        }
      };

      ws.onclose = () => {
        setChatConnected(false);
        if (cancelled) return; // this effect run was superseded — don't reconnect on its behalf
        // Exponential Backoff Reconnection (Max 30s)
        const timeout = Math.min(1000 * (2 ** attempt), 30000);
        attempt++;
        reconnectTimeoutRef.current = setTimeout(connectWebSocket, timeout);
      };
    };

    connectWebSocket();

    return () => {
      cancelled = true;
      clearTimeout(reconnectTimeoutRef.current);
      setChatConnected(false);
      const ws = chatWsRef.current;
      if (ws) {
        ws.onclose = null;
        if (ws.readyState === WebSocket.OPEN) {
          ws.close();
        }
        // If it's still CONNECTING, don't force-close it here — the
        // onopen handler above sees `cancelled` and closes it immediately
        // once the handshake completes, which is the unambiguous point to
        // do it from (see the long comment on onopen above).
      }
    };
  }, [selectedLoad?.rawId, apiUrl, fetchDashboardData]);

  // ── Chat Send Handler (WebSocket Fast-Path -> REST Fallback) ──────────────
  const handleSendMessage = async (e, overrideText) => {
    e?.preventDefault?.();
    const text = (overrideText ?? chatInput).trim();
    if (!text || !selectedLoad) return;

    const token = localStorage.getItem("user_token") || localStorage.getItem("access_token");
    setChatInput("");

    // Optimistic echo: show the message immediately instead of waiting on
    // the server round-trip. It's marked `pending` (slightly faded in the
    // UI) until the real copy comes back over the socket, at which point
    // the reconciliation in the WS onmessage handler below swaps it out —
    // so you never see a duplicate, but you also never wait to see your
    // own message.
    const tempId = `pending-${Date.now()}`;
    setMessages(prev => [...prev, {
      id: tempId, content: text, sender: user?.id,
      sender_name: "You", created_at: new Date().toISOString(), pending: true,
    }]);

    // Fast-path: Push via WS instantly
    if (chatWsRef.current && chatWsRef.current.readyState === WebSocket.OPEN) {
      chatWsRef.current.send(JSON.stringify({ type: "message", message: text, client_id: tempId }));
    } else {
      // Fallback: REST API — still lands immediately for the sender since
      // it's already showing optimistically above; the socket reconnect
      // (auto-retrying with backoff) will pick up the confirmed copy, or
      // the next chat/history/ refetch will.
      try {
        await fetch(`${apiUrl}/api/loads/assignments/${selectedLoad.rawId}/chat/send/`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
          body: JSON.stringify({ text })
        });
      } catch (err) { console.error("Chat send error:", err); }
    }
    // Sending a message implies typing stopped.
    if (chatWsRef.current?.readyState === WebSocket.OPEN && myTypingSentRef.current) {
      chatWsRef.current.send(JSON.stringify({ type: "typing", is_typing: false }));
      myTypingSentRef.current = false;
    }
  };

  // Debounced typing sender: tells the driver "dispatcher is typing…" as
  // soon as they start, and clears it 3s after they stop — matching the
  // read_receipt/typing infrastructure that already existed on the
  // backend but was never called from here.
  const myTypingStopTimeoutRef = useRef(null);
  const handleChatInputChange = (value) => {
    setChatInput(value);
    if (!chatWsRef.current || chatWsRef.current.readyState !== WebSocket.OPEN) return;
    if (!myTypingSentRef.current) {
      chatWsRef.current.send(JSON.stringify({ type: "typing", is_typing: true }));
      myTypingSentRef.current = true;
    }
    clearTimeout(myTypingStopTimeoutRef.current);
    myTypingStopTimeoutRef.current = setTimeout(() => {
      if (chatWsRef.current?.readyState === WebSocket.OPEN) {
        chatWsRef.current.send(JSON.stringify({ type: "typing", is_typing: false }));
      }
      myTypingSentRef.current = false;
    }, 3000);
  };

  const filteredLoads = orderFilter === "All" ? loads : loads.filter(l => l.status === orderFilter);

  // Clicking a stat card jumps straight to the matching filtered view —
  // these used to be static, non-interactive divs.
  // Clicking a stat card jumps to the matching filtered view AND scrolls
  // the Orders panel into view — a visible confirmation the click did
  // something, even in the edge case where the resulting filter happens to
  // match zero orders (e.g. clicking "Delayed" when nothing is delayed),
  // where an empty list alone could look indistinguishable from a dead button.
  const applyOrderFilter = (filter) => {
    setOrderFilter(filter);
    ordersSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };
  const SUMMARY_CARDS = [
    { label: "Total Orders", val: stats?.total_orders || 0, color: "blue", icon: "📋", onClick: () => applyOrderFilter("All") },
    { label: "In Progress", val: stats?.in_progress || 0, color: "green", icon: "🚚", onClick: () => applyOrderFilter("In Progress") },
    { label: "Delivered", val: stats?.delivered || 0, color: "purple", icon: "✅", onClick: () => applyOrderFilter("Completed") },
    { label: "Delayed", val: stats?.delayed || 0, color: "red", icon: "⏱", onClick: () => applyOrderFilter("Delayed") },
    { 
      label: "Active Vehicles", 
      val: `${stats?.active_vehicles || 0}/${stats?.total_vehicles || 0}`, 
      color: "blue", icon: "🚙", 
      prog: stats?.total_vehicles ? Math.round((stats.active_vehicles / stats.total_vehicles) * 100) : 0,
      onClick: () => { setOrderFilter("In Progress"); mapSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); },
    }
  ];

  // 3-dots menu action in the vehicle detail card — pauses/resumes GPS
  // reporting for this load. Real backend endpoint (loads/views.py::
  // ToggleTrackingView) already existed; this just calls it.
  const handleToggleTracking = async (load) => {
    if (!load?.rawId) return;
    const token = localStorage.getItem("user_token") || localStorage.getItem("access_token");
    const nextEnabled = !load.isTrackingActive;
    try {
      const res = await fetch(`${apiUrl}/api/loads/assignments/${load.rawId}/gps/toggle/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ enabled: nextEnabled }),
      });
      if (res.ok) {
        setSelectedLoad(prev => prev && prev.rawId === load.rawId ? { ...prev, isTrackingActive: nextEnabled } : prev);
        setLoads(prev => prev.map(l => l.rawId === load.rawId ? { ...l, isTrackingActive: nextEnabled } : l));
      } else {
        const err = await res.json().catch(() => ({}));
        pushToast({ driverName: "Tracking", content: err.error || "Couldn't toggle tracking.", onClick: null });
      }
    } catch (err) {
      console.error("Toggle tracking error:", err);
    }
  };

  const handleSelectLoad = (load) => {
    setSelectedLoad(load);
    setDetailOpen(true);
    setMobileView("detail");
    // Opening a conversation clears its unread badge optimistically —
    // GET .../chat/history/ (fired by the effect above) marks it read server-side.
    setLoads(prev => prev.map(l => l.rawId === load.rawId ? { ...l, raw: { ...l.raw, unread_count: 0 } } : l));
  };

  // Map marker click: select the vehicle's load (drives the right panel)
  // AND pop the driver-info card on top of the map.
  const handleSelectVehicleFromMap = useCallback((rawId) => {
    setLoads(prevLoads => {
      const load = prevLoads.find(l => String(l.rawId) === String(rawId));
      if (load) {
        setSelectedLoad(load);
        setDetailOpen(true);
        setMapPopupDrv(load);
      }
      return prevLoads;
    });
  }, []);

  const MapControls = ({ compact = false }) => (
    <div className="flex items-center gap-1.5">
      <button disabled className="text-xs px-2.5 py-1.5 rounded-lg font-semibold bg-green-50 text-green-700 border border-green-200 opacity-80 cursor-default">
        {compact ? "Live" : "🟢 Live Sync"}
      </button>
      {!compact && (
        <button className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 flex items-center gap-1 font-semibold">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/></svg>
          Filter
        </button>
      )}
      <button onClick={() => setGpsModal(true)} className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg font-bold flex items-center gap-1.5 transition-colors">
        📡 {compact ? "GPS" : "GPS Recall"}
      </button>
    </div>
  );

  const BackButton = ({ to }) => (
    <button onClick={() => setMobileView(to)} className="text-blue-600">
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/></svg>
    </button>
  );

  return (
    <>
      {/* DESKTOP LAYOUT */}
      <div className="flex-1 overflow-hidden hidden lg:flex h-full">
        <div className="flex-1 overflow-y-auto flex flex-col min-w-0">
          <div className="grid grid-cols-5 gap-3 p-4 pb-3 flex-shrink-0">
            {SUMMARY_CARDS.map((c, i) => <SummaryCard key={i} c={c} onClick={c.onClick}/>)}
          </div>
          <div className="flex gap-3 px-4 pb-4 flex-1 min-h-0">
            {/* Orders panel */}
            <div ref={ordersSectionRef} className="w-72 flex-shrink-0 bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col overflow-hidden">
              <div className="px-4 pt-4 pb-2 flex-shrink-0">
                <h2 className="font-bold text-gray-900 text-base mb-3">Orders</h2>
                <div className="flex gap-1 border-b border-gray-100 pb-2">
                  {["All","In Progress","Pending","Completed","Delayed"].map(f => (
                    <button key={f} onClick={() => setOrderFilter(f)}
                      className={`px-2 py-1 rounded text-xs font-semibold transition-colors whitespace-nowrap ${
                        orderFilter===f ? "text-blue-700 border-b-2 border-blue-600" : "text-gray-400 hover:text-gray-700"
                      }`}
                    >{f}</button>
                  ))}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto custom-scrollbar">
                {filteredLoads.map(load => (
                  <OrderRow key={load.rawId} load={load}
                    selected={selectedLoad?.rawId === load.rawId}
                    onClick={() => handleSelectLoad(load)}
                  />
                ))}
                {filteredLoads.length === 0 && (
                  <p className="text-xs text-gray-400 text-center mt-6">No orders found.</p>
                )}
              </div>
              <div className="px-4 py-3 border-t border-gray-100 flex-shrink-0">
                <button className="text-sm text-blue-600 hover:text-blue-800 font-semibold">View all orders →</button>
              </div>
            </div>

            {/* Live Tracking map */}
            <div ref={mapSectionRef} className="flex-1 bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col overflow-hidden min-w-0">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-900">Live Tracking</span>
                  {selectedLoad?.status === "In Progress" && <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"/>}
                </div>
                <MapControls/>
              </div>
              <div className="flex-1 relative overflow-hidden min-h-0 bg-slate-50 dark:bg-slate-900">
                <LiveTrackingMap
                  loads={loads}
                  selectedLoadId={selectedLoad?.rawId}
                  onSelectVehicle={handleSelectVehicleFromMap}
                  darkMode={darkMode}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right panel */}
        <div className="w-80 flex-shrink-0 border-l border-gray-200 bg-gray-50 flex flex-col overflow-hidden">
          <RightPanel
            rightTab={rightTab} setRightTab={setRightTab}
            selectedLoad={selectedLoad} 
            detailOpen={detailOpen} setDetailOpen={setDetailOpen}
            openGPSRecall={() => setGpsModal(true)}
            messages={messages}
            chatInput={chatInput}
            setChatInput={handleChatInputChange}
            onSendMessage={handleSendMessage}
            alerts={alerts}
            gpsHistory={gpsHistory}
            currentUserId={user?.id}
            loads={loads}
            onSelectLoad={handleSelectLoad}
            chatConnected={chatConnected}
            driverTyping={driverTyping}
            onToggleTracking={handleToggleTracking}
          />
        </div>
      </div>

      {/* MOBILE LAYOUT */}
      <div className="flex-1 overflow-hidden flex flex-col lg:hidden">
        {mobileView === "dashboard" && (
          <div className="flex-1 overflow-y-auto pb-16 custom-scrollbar">
            <div className="grid grid-cols-2 gap-2 p-3">
              {SUMMARY_CARDS.map((c, i) => <SummaryCard key={i} c={c} compact onClick={c.onClick}/>)}
            </div>
            <div className="bg-white rounded-2xl mx-3 mb-3 shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-4 pt-4 pb-2">
                <h2 className="font-bold text-gray-900 text-base mb-2">Orders</h2>
                <div className="flex gap-1 border-b border-gray-100 pb-2 overflow-x-auto custom-scrollbar">
                  {["All","In Progress","Pending","Completed","Delayed"].map(f => (
                    <button key={f} onClick={() => setOrderFilter(f)}
                      className={`px-2 py-1 rounded text-xs font-semibold whitespace-nowrap ${orderFilter===f?"text-blue-700 border-b-2 border-blue-600":"text-gray-400"}`}
                    >{f}</button>
                  ))}
                </div>
              </div>
              {filteredLoads.slice(0,4).map(load => (
                <OrderRow key={load.rawId} load={load} selected={selectedLoad?.rawId === load.rawId} onClick={() => handleSelectLoad(load)} />
              ))}
              <div className="px-4 py-3 border-t">
                <button onClick={() => setMobileView("orders")} className="text-sm text-blue-600 font-semibold">View all orders →</button>
              </div>
            </div>
            <div className="bg-white rounded-2xl mx-3 mb-3 shadow-sm border border-gray-100 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <span className="font-bold text-gray-900">Live Tracking</span>
                <button onClick={() => setMobileView("map")} className="text-xs text-blue-600 font-semibold">Full Map →</button>
              </div>
              <div className="h-48 relative bg-slate-50">
                <LiveTrackingMap loads={loads} selectedLoadId={selectedLoad?.rawId} onSelectVehicle={handleSelectVehicleFromMap} darkMode={darkMode}/>
              </div>
            </div>
          </div>
        )}

        {mobileView === "orders" && (
          <div className="flex-1 overflow-hidden flex flex-col pb-16">
            <div className="bg-white border-b px-4 py-3 flex-shrink-0">
              <div className="flex items-center gap-3 mb-2">
                <BackButton to="dashboard"/>
                <h2 className="font-bold text-gray-900">Orders</h2>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto bg-white custom-scrollbar">
              {filteredLoads.map(load => (
                <OrderRow key={load.rawId} load={load} selected={selectedLoad?.rawId === load.rawId} onClick={() => handleSelectLoad(load)} />
              ))}
            </div>
          </div>
        )}

        {mobileView === "map" && (
          <div className="flex-1 flex flex-col pb-16">
            <div className="bg-white border-b px-4 py-3 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <BackButton to="dashboard"/>
                <span className="font-bold text-gray-900">Live Tracking</span>
              </div>
              <MapControls compact/>
            </div>
            <div className="flex-1 relative overflow-hidden bg-slate-50">
              <LiveTrackingMap loads={loads} selectedLoadId={selectedLoad?.rawId} onSelectVehicle={handleSelectVehicleFromMap} darkMode={darkMode}/>
            </div>
          </div>
        )}

        {mobileView === "detail" && (
          <div className="flex-1 overflow-hidden flex flex-col pb-16 bg-gray-50">
            <div className="bg-white border-b px-4 py-3 flex items-center gap-3 flex-shrink-0">
              <BackButton to="dashboard"/>
              <span className="font-bold text-gray-900">Detail / Chat</span>
            </div>
            <div className="flex-1 overflow-hidden flex flex-col">
              <RightPanel
                rightTab={rightTab} setRightTab={setRightTab}
                selectedLoad={selectedLoad}
                detailOpen={detailOpen} setDetailOpen={setDetailOpen}
                openGPSRecall={() => setGpsModal(true)}
                messages={messages} chatInput={chatInput} setChatInput={handleChatInputChange} onSendMessage={handleSendMessage}
                alerts={alerts} gpsHistory={gpsHistory} currentUserId={user?.id}
                loads={loads} onSelectLoad={handleSelectLoad}
                chatConnected={chatConnected} driverTyping={driverTyping} onToggleTracking={handleToggleTracking}
              />
            </div>
          </div>
        )}

        <BottomBar mobileView={mobileView} setMobileView={setMobileView} rightTab={rightTab} setRightTab={setRightTab} />
      </div>

      {gpsModal && (
        <GPSModal load={selectedLoad} apiUrl={apiUrl} onClose={() => setGpsModal(false)} />
      )}
      {mapPopupDrv && (
        <MapPopupModal
          load={mapPopupDrv}
          onClose={() => setMapPopupDrv(null)}
          onOpenChat={() => { setMapPopupDrv(null); setRightTab("chat"); }}
        />
      )}
      {activePanel === "drivers" && (
        <DriversPanel apiUrl={apiUrl} onClose={() => setActivePanel(null)} />
      )}
      {activePanel === "vehicles" && (
        <VehiclesPanel apiUrl={apiUrl} onClose={() => setActivePanel(null)} />
      )}
      {activePanel === "reports" && (
        <ReportsPanel apiUrl={apiUrl} onClose={() => setActivePanel(null)} />
      )}
      {activePanel === "settings" && (
        <SettingsPanel apiUrl={apiUrl} user={user} onClose={() => setActivePanel(null)} />
      )}
      {activePanel === "documents" && (
        <DocumentsPanel onClose={() => setActivePanel(null)} />
      )}

      {/* Pop-up message notifications — "chat message pop out" for the
          dispatcher: fires for a new message whether or not that
          conversation is currently open. Click one to jump straight into
          that chat; auto-dismisses after 5s either way. */}
      <div className="fixed top-4 right-4 z-[60] flex flex-col gap-2 w-80 max-w-[calc(100vw-2rem)]">
        {toasts.map(t => (
          <button
            key={t.id}
            onClick={() => { t.onClick?.(); setToasts(prev => prev.filter(x => x.id !== t.id)); }}
            className="bg-white border border-gray-200 shadow-xl rounded-xl p-3 flex items-start gap-3 text-left animate-fade-in-up hover:border-blue-300 transition-colors"
          >
            <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center font-bold text-blue-700 text-xs flex-shrink-0">
              {(t.driverName || "?").split(" ").map(p => p[0]).slice(0, 2).join("").toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-800">{t.driverName}</p>
              <p className="text-xs text-gray-500 truncate">{t.content}</p>
            </div>
            <span
              onClick={(e) => { e.stopPropagation(); setToasts(prev => prev.filter(x => x.id !== t.id)); }}
              className="text-gray-300 hover:text-gray-500 text-sm leading-none flex-shrink-0"
            >×</span>
          </button>
        ))}
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 20px; }
        .custom-scrollbar:hover::-webkit-scrollbar-thumb { background-color: #94a3b8; }
        @keyframes fade-in-up { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in-up { animation: fade-in-up 0.2s ease-out; }

        /* ── Dark mode ─────────────────────────────────────────────────────
           Plain CSS overrides scoped under .dispatcher-dark (set on the root
           wrapper in DispatcherDashboard.jsx), rather than relying on
           Tailwind's dark: variant — this works regardless of whether the
           project's Tailwind config even has darkMode:'class' enabled.
           Covers every background/text/border utility actually used across
           Includes/ and Pages/, so it doesn't require editing each
           component file individually. Applies everywhere .dispatcher-dark
           is an ancestor, including Sidebar/TopBar even though this <style>
           tag itself lives inside the routed page — CSS cascades by DOM
           position, not by which React component rendered the tag. */
        .dispatcher-dark { color-scheme: dark; }
        .dispatcher-dark .bg-white { background-color: #1e293b !important; }
        .dispatcher-dark .bg-gray-50 { background-color: #0f172a !important; }
        .dispatcher-dark .bg-gray-100 { background-color: #1e293b !important; }
        .dispatcher-dark .bg-slate-50 { background-color: #0f172a !important; }
        .dispatcher-dark .bg-gray-300 { background-color: #475569 !important; }
        .dispatcher-dark .bg-blue-50 { background-color: rgba(37,99,235,0.18) !important; }
        .dispatcher-dark .bg-blue-100 { background-color: rgba(37,99,235,0.25) !important; }
        .dispatcher-dark .bg-orange-100 { background-color: rgba(245,158,11,0.22) !important; }
        .dispatcher-dark .bg-green-100 { background-color: rgba(22,163,74,0.22) !important; }
        .dispatcher-dark .bg-red-100 { background-color: rgba(220,38,38,0.22) !important; }
        .dispatcher-dark .bg-amber-50 { background-color: rgba(245,158,11,0.15) !important; }

        .dispatcher-dark .text-gray-900 { color: #f1f5f9 !important; }
        .dispatcher-dark .text-gray-800 { color: #e2e8f0 !important; }
        .dispatcher-dark .text-gray-700 { color: #cbd5e1 !important; }
        .dispatcher-dark .text-gray-600 { color: #94a3b8 !important; }
        .dispatcher-dark .text-gray-500 { color: #94a3b8 !important; }
        .dispatcher-dark .text-gray-400 { color: #64748b !important; }
        .dispatcher-dark .text-gray-300 { color: #475569 !important; }
        .dispatcher-dark .text-slate-900 { color: #f1f5f9 !important; }
        .dispatcher-dark .text-slate-400 { color: #64748b !important; }
        .dispatcher-dark .placeholder-gray-300::placeholder { color: #475569 !important; }

        .dispatcher-dark .border-gray-100 { border-color: #334155 !important; }
        .dispatcher-dark .border-gray-200 { border-color: #334155 !important; }
        .dispatcher-dark .border-gray-300 { border-color: #475569 !important; }
        .dispatcher-dark .border-slate-100 { border-color: #334155 !important; }
        .dispatcher-dark .border-slate-200 { border-color: #334155 !important; }
        .dispatcher-dark .divide-gray-100 > * + * { border-color: #334155 !important; }

        .dispatcher-dark .shadow-sm,
        .dispatcher-dark .shadow-md,
        .dispatcher-dark .shadow-xl,
        .dispatcher-dark .shadow-2xl { box-shadow: 0 4px 16px rgba(0,0,0,0.45) !important; }

        .dispatcher-dark .hover\\:bg-gray-50:hover { background-color: #273449 !important; }
        .dispatcher-dark .hover\\:bg-gray-100:hover { background-color: #334155 !important; }
        .dispatcher-dark .hover\\:border-blue-200:hover,
        .dispatcher-dark .hover\\:border-blue-300:hover { border-color: #3b82f6 !important; }

        .dispatcher-dark .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #475569; }
        .dispatcher-dark .custom-scrollbar:hover::-webkit-scrollbar-thumb { background-color: #64748b; }
      `}} />
    </>
  );
}